import type { RowData, Table as TanStackTable } from "@tanstack/react-table";
import type { ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";
import { PaginatedList } from "../../molecules/PaginatedList";
import { useTableContext } from "./TableContext";
import { TableEndContent } from "./TableEndContent";
import { TableRow } from "./TableRow";
import { TableSortableRow } from "./TableSortableRow";
import type { TableScrollbar } from "./types";

export interface TableRowListProps<TRow extends RowData> {
  table: TanStackTable<TRow>;
  viewportRef: Ref<HTMLDivElement>;
  scrollbar: TableScrollbar;
  label: string;
  minWidth: number;
  onBottomReached?: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  emptyContent: ReactNode | undefined;
  onRowClick?: (row: TRow) => void;
  onRowContextMenu?: (row: TRow) => void;
  resetKey: string | undefined;
}

export const TableRowList = <TRow extends RowData>({
  table,
  viewportRef,
  scrollbar,
  label,
  minWidth,
  onBottomReached,
  isLoading,
  isLoadingMore,
  emptyContent,
  onRowClick,
  onRowContextMenu,
  resetKey,
}: TableRowListProps<TRow>) => {
  const { t } = useTranslation();
  const { canReorder } = useTableContext();
  const rows = table.getRowModel().rows;

  const list = (
    <PaginatedList
      className="min-h-0 flex-1 overflow-x-auto"
      scrollOptions={{ type: scrollbar }}
      viewportRef={viewportRef}
      contentMinWidth={minWidth}
      label={label}
      onBottomReached={onBottomReached}
      isLoading={isLoading}
      emptyMessage={emptyContent ?? t("No results")}
      resetKey={resetKey}
    >
      {/* One flat array, so the list's child count sees zero rows as empty; a `false` placeholder
          for the loading row would count as a child and hide the empty state. */}
      {[
        ...rows.map((row, index) =>
          canReorder ? (
            <TableSortableRow
              key={row.id}
              row={row}
              index={index}
              isLast={index === rows.length - 1}
              minWidth={minWidth}
              onClick={onRowClick}
              onContextMenu={onRowContextMenu}
            />
          ) : (
            <TableRow
              key={row.id}
              row={row}
              index={index}
              isLast={index === rows.length - 1}
              minWidth={minWidth}
              onClick={onRowClick}
              onContextMenu={onRowContextMenu}
            />
          )
        ),
        ...(isLoadingMore ? [<TableEndContent key="table-end" />] : []),
      ]}
    </PaginatedList>
  );

  return list;
};
